import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GraduationCap, Plus, Pencil, Trash2, Users, ArchiveRestore, Award } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import type { ListViewContextAction, ListViewHeaderAction } from "../../../ui/components/ListView";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { useAuth } from "../../lib/auth/hooks";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useI18n } from "../../lib/i18n";
import { useCertificationContentDefinitions } from "../../lib/i18n/useCertificationContentDefinitions";
import { isContentLocKey } from "../../lib/i18n/contentKeys";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { deleteCertificationModule } from "../../lib/api/certifications";
import { downloadCertificatePdf } from "../../lib/certificate/certificatePdf";
import {
  certificationModulesQueryOptions,
  certificationQueryKeys,
  certificationTechniciansQueryOptions,
  rewardRulesQueryOptions,
} from "../../lib/api/certification-queries";
import type { CertificationModule, Technician } from "../../lib/api/certifications";
import type { RewardRule } from "../../lib/api/certification-reward-rules";
import { vars } from "../../../ui/styles/theme.css";
import { ModuleFormModal } from "./ModuleFormModal";
import { RewardRuleCard } from "./RewardRuleCard";
import { RewardRuleFormModal } from "./RewardRuleFormModal";
import { ListViewCertificationModuleCard } from "../../../ui/components/ListView/components/ListViewCertificationModuleCard";
import { ListViewTechnicianCard } from "../../../ui/components/ListView/components/ListViewTechnicianCard";
import { useListImportExport } from "../data-transfer/useListImportExport";
import * as layout from "../organizations/OrganizationEditPage.css";

type TechnicianRow = Technician & { id: string };

const SECTION_IDS = {
  modules: "certification-modules",
  technicians: "certification-technicians",
  rewardRules: "certification-reward-rules",
} as const;

export function CertificationModulesListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { accessToken } = useAuth();
  const { t, currentLanguage } = useI18n();
  const { resolve } = useCertificationContentDefinitions();
  const resolveModuleTitle = React.useCallback(
    (title: string) => (isContentLocKey(title) ? resolve(title) || "…" : title),
    [resolve],
  );
  // Tokens live in a shared cookie, not localStorage (see session-cookie.ts) —
  // this fallback matches the pattern used elsewhere in the auth hooks.
  const token = accessToken ?? getAuthCookie("access_token");
  const { confirm: confirmModule, ConfirmModal: ModuleConfirmModal } = useConfirmModal();
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse("certification-modules-list");

  const modulesQuery = useQuery({
    ...certificationModulesQueryOptions(token ?? "", { includeInactive: true }),
    enabled: Boolean(token),
  });
  const techniciansQuery = useQuery({
    ...certificationTechniciansQueryOptions(token ?? ""),
    enabled: Boolean(token),
  });
  const rewardRulesQuery = useQuery({
    ...rewardRulesQueryOptions(token ?? ""),
    enabled: Boolean(token),
  });

  const modules = modulesQuery.data ?? [];
  const modulesLoading = modulesQuery.isLoading;
  const modulesError = modulesQuery.error
    ? (modulesQuery.error as Error).message || t("pro.system.certification.error.loadModules")
    : null;

  const technicians = (techniciansQuery.data ?? []) as TechnicianRow[];
  const techniciansLoading = techniciansQuery.isLoading;
  const techniciansError = techniciansQuery.error
    ? (techniciansQuery.error as Error).message || t("pro.system.certification.error.loadTechnicians")
    : null;

  const [moduleModalOpen, setModuleModalOpen] = React.useState(false);
  const [editingModule, setEditingModule] = React.useState<CertificationModule | undefined>(undefined);
  const [rewardRuleModalOpen, setRewardRuleModalOpen] = React.useState(false);
  const [editingRewardRule, setEditingRewardRule] = React.useState<RewardRule | undefined>(undefined);

  const setModules = React.useCallback(
    (updater: (prev: CertificationModule[]) => CertificationModule[]) => {
      queryClient.setQueryData<CertificationModule[]>(
        certificationQueryKeys.modules({ includeInactive: true }),
        (prev) => updater(prev ?? []),
      );
    },
    [queryClient],
  );

  const setRewardRules = React.useCallback(
    (updater: (prev: RewardRule[]) => RewardRule[]) => {
      queryClient.setQueryData<RewardRule[]>(certificationQueryKeys.rewardRules(), (prev) =>
        updater(prev ?? []),
      );
    },
    [queryClient],
  );

  const rewardRules = rewardRulesQuery.data ?? [];
  const rewardRulesLoading = rewardRulesQuery.isLoading;
  const rewardRulesError = rewardRulesQuery.error
    ? (rewardRulesQuery.error as Error).message || t("pro.system.rewardRules.error.load")
    : null;

  const modulesListKey = React.useMemo(
    () =>
      modules
        .map(
          (m) =>
            `${m.id}:${m.isActive ? 1 : 0}:${m.title}:${m.slug}:${m.heroImageUrl ?? ""}:${m.sortOrder}`,
        )
        .join("|"),
    [modules],
  );

  const fetchModulesList = React.useCallback((params: ListParams): Promise<ListResult<CertificationModule>> => {
    const start = (params.page - 1) * params.limit;
    const filtered = params.search
      ? modules.filter((m) =>
          [resolveModuleTitle(m.title), m.slug, m.description ?? ""].some((v) =>
            v.toLowerCase().includes(params.search!.toLowerCase()),
          )
        )
      : modules;
    return Promise.resolve({ data: filtered.slice(start, start + params.limit), total: filtered.length });
  }, [modules, resolveModuleTitle]);

  const fetchTechniciansList = React.useCallback((params: ListParams): Promise<ListResult<TechnicianRow>> => {
    const start = (params.page - 1) * params.limit;
    const filtered = params.search
      ? technicians.filter((tech) =>
          [tech.name, tech.email].some((v) => v.toLowerCase().includes(params.search!.toLowerCase()))
        )
      : technicians;
    return Promise.resolve({ data: filtered.slice(start, start + params.limit), total: filtered.length });
  }, [technicians]);

  const handleTechnicianClick = React.useCallback((row: TechnicianRow) => {
    navigate({ to: "/workspace/users/$userId", params: { userId: row.userId } });
  }, [navigate]);

  const downloadTechnicianCertificate = React.useCallback(
    (technician: TechnicianRow, moduleId: string) => {
      const progress = technician.modules.find((m) => m.moduleId === moduleId);
      if (!progress) return;
      const catalog = modules.find((m) => m.id === moduleId);
      const courseTitle = resolve(progress.moduleTitle).trim() || progress.moduleTitle;
      const headerOverride = catalog?.certificateHeaderText
        ? resolve(catalog.certificateHeaderText).trim()
        : "";
      const courseOverride = catalog?.certificateCourseText
        ? resolve(catalog.certificateCourseText).trim()
        : "";
      downloadCertificatePdf({
        recipientName: technician.name,
        courseTitle: courseOverride || courseTitle,
        completedAt: progress.completedAt,
        certificateNumber: progress.certificateNumber ?? null,
        labels: {
          heading: headerOverride || t("certification.certificate.heading"),
          presentedTo: t("certification.certificate.presentedTo"),
          completionLine: t("certification.certificate.completionLine"),
          completedOn: t("certification.quiz.completedOn"),
          certificateNumber: t("certification.certificate.numberLabel"),
          issuedBy: t("certification.certificate.issuedBy"),
        },
        locale: currentLanguage?.code,
      });
    },
    [modules, resolve, t, currentLanguage?.code],
  );

  const moduleColumns = React.useMemo<ColumnDefinition<CertificationModule>[]>(() => [
    { id: 'title', label: t("pro.system.certification.col.title"), render: (row) => <span style={{ fontWeight: 600 }}>{resolveModuleTitle(row.title)}</span> },
    { id: 'slug', label: t("pro.system.certification.col.slug"), render: (row) => <code style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.slug}</code> },
    { id: 'isActive', label: t("pro.system.certification.col.status"), render: (row) => <span>{row.isActive ? t("pro.system.certification.status.active") : t("pro.system.certification.status.inactive")}</span> },
  ], [t, resolveModuleTitle]);

  const technicianColumns = React.useMemo<ColumnDefinition<TechnicianRow>[]>(() => [
    { id: 'name', label: t("pro.system.certification.col.name"), render: (row) => <span style={{ fontWeight: 600 }}>{row.name}</span> },
    { id: 'email', label: t("pro.system.certification.col.email"), render: (row) => <span>{row.email}</span> },
  ], [t]);

  const [moduleSelectedIds, setModuleSelectedIds] = React.useState<string[]>([]);

  const exportPreviewItems = React.useMemo(
    () =>
      modules.map((m) => ({
        id: m.id,
        label: m.title,
        matchKey: m.slug,
        imageUrl: m.heroImageUrl ?? undefined,
      })),
    [modules],
  );

  const {
    headerActions: ieHeaderActions,
    exportContextAction,
    modals: importExportModals,
  } = useListImportExport({
    entity: "certification-modules",
    entityLabel: "Modules",
    enableImport: true,
    enableExport: true,
    totalCount: modules.length,
    selectedIds: moduleSelectedIds,
    previewItems: exportPreviewItems,
    defaultFormat: "zip",
    onImported: () => {
      void queryClient.invalidateQueries({
        queryKey: certificationQueryKeys.modules({ includeInactive: true }),
      });
    },
  });

  const getModuleContextActions = React.useCallback((row: CertificationModule): ListViewContextAction[] => {
    const actions: ListViewContextAction[] = [
    {
      id: "edit",
      label: t("shared.common.edit"),
      icon: <Pencil size={14} />,
      bulk: false,
      onClick: () => { setEditingModule(row); setModuleModalOpen(true); },
    },
    {
      id: "delete",
      label: t("shared.common.delete"),
      icon: <Trash2 size={14} />,
      danger: true,
      bulk: false,
      onClick: async () => {
        if (!token) return;
        const confirmed = await confirmModule(
          t("pro.system.certification.confirm.deleteModuleTitle"),
          t("pro.system.certification.confirm.deleteModuleBody", { title: resolveModuleTitle(row.title) }),
          t("shared.common.delete"),
          "danger",
        );
        if (!confirmed) return;
        try {
          await deleteCertificationModule(token, row.id);
          setModules((prev) => prev.filter((m) => m.id !== row.id));
        } catch { /* ignore */ }
      },
    },
    ];
    if (exportContextAction) {
      actions.push({
        ...exportContextAction,
        onClick: (ids) => exportContextAction.onClick(ids),
      });
    }
    return actions;
  }, [token, confirmModule, setModules, t, resolveModuleTitle, exportContextAction]);

  const handleModuleClick = React.useCallback((row: CertificationModule) => {
    navigate({ to: "/system/certifications/$moduleId", params: { moduleId: row.id } });
  }, [navigate]);

  const moduleHeaderActions: ListViewHeaderAction[] = [
    ...ieHeaderActions,
    {
      id: "add-module",
      label: t("pro.system.certification.modules.add"),
      icon: <Plus size={14} />,
      color: "primary",
      onClick: () => {
        setEditingModule(undefined);
        setModuleModalOpen(true);
      },
    },
  ];

  const sectionLayout = bindSectionLayout([
    {
      id: SECTION_IDS.modules,
      label: t("pro.system.certification.modules.title"),
      icon: <GraduationCap size={14} />,
    },
    {
      id: SECTION_IDS.technicians,
      label: t("pro.system.certification.technicians.title"),
      icon: <Users size={14} />,
    },
    {
      id: SECTION_IDS.rewardRules,
      label: t("pro.system.rewardRules.title"),
      icon: <Award size={14} />,
    },
  ]);

  return (
    <>
      <DetailPageLayout
        header={{
          media: <GraduationCap size={48} />,
          title: t("pro.system.certification.title"),
          subtitle: t("pro.system.certification.subtitle"),
        }}
        {...sectionLayout}
      >
        <CollapsibleSection
          id={SECTION_IDS.modules}
          title={t("pro.system.certification.modules.title")}
          subtitle={
            !modulesLoading && modules.length > 0
              ? `${modules.length} module${modules.length !== 1 ? "s" : ""}`
              : undefined
          }
          icon={<GraduationCap size={20} />}
          expanded={isExpanded(SECTION_IDS.modules)}
          onToggleExpanded={() => toggle(SECTION_IDS.modules)}
        >
          <div className={layout.cardInner}>
            {modulesError && (
              <div style={{ marginBottom: 16 }}>
                <Callout variant="error">{modulesError}</Callout>
              </div>
            )}
            {modulesLoading && modules.length === 0 ? (
              <Text level="body-small" style={{ color: vars.color.fgMuted }}>
                {t("shared.common.loading")}
              </Text>
            ) : (
              <ListView<CertificationModule>
                listId="certification-modules"
                contextKey={modulesListKey}
                queryOptions={{ staleTime: Infinity }}
                columns={moduleColumns}
                fetchList={fetchModulesList}
                enabledViews={['stack', 'deck']}
                renderStackCard={(row, ctx) => (
                  <ListViewCertificationModuleCard layout="stack" module={row} onClick={ctx.onClick} />
                )}
                renderCard={(row, ctx) => (
                  <ListViewCertificationModuleCard layout="deck" module={row} onClick={ctx.onClick} />
                )}
                onRowClick={handleModuleClick}
                getContextActions={getModuleContextActions}
                enableSelection
                onSelectionChange={setModuleSelectedIds}
                compact
                deckCardHeight={220}
                disableLocalPersistence
                defaultPageSize={10}
                emptyTitle={t("pro.system.certification.modules.emptyTitle")}
                emptySubtitle={t("pro.system.certification.modules.emptySubtitle")}
                headerActions={moduleHeaderActions}
              />
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          id={SECTION_IDS.technicians}
          title={t("pro.system.certification.technicians.title")}
          subtitle={
            !techniciansLoading && technicians.length > 0
              ? `${technicians.length} technician${technicians.length !== 1 ? "s" : ""}`
              : undefined
          }
          icon={<Users size={20} />}
          expanded={isExpanded(SECTION_IDS.technicians)}
          onToggleExpanded={() => toggle(SECTION_IDS.technicians)}
        >
          <div className={layout.cardInner}>
            {techniciansError && (
              <div style={{ marginBottom: 16 }}>
                <Callout variant="error">{techniciansError}</Callout>
              </div>
            )}
            {techniciansLoading && technicians.length === 0 ? (
              <Text level="body-small" style={{ color: vars.color.fgMuted }}>
                {t("shared.common.loading")}
              </Text>
            ) : (
              <ListView<TechnicianRow>
                listId="certification-technicians"
                contextKey={String(technicians.length)}
                queryOptions={{ staleTime: Infinity }}
                columns={technicianColumns}
                fetchList={fetchTechniciansList}
                enabledViews={['deck']}
                renderCard={(row, ctx) => (
                  <ListViewTechnicianCard
                    layout="deck"
                    technician={row}
                    onClick={ctx.onClick}
                    showLastSignIn
                    onDownloadCertificate={(moduleId) => downloadTechnicianCertificate(row, moduleId)}
                    catalogModules={modules.map((m) => ({
                      id: m.id,
                      title: m.title,
                      trophyImageUrl: m.trophyImageUrl,
                      heroImageUrl: m.heroImageUrl,
                      description: m.description,
                      sortOrder: m.sortOrder,
                      isActive: m.isActive,
                    }))}
                  />
                )}
                onRowClick={handleTechnicianClick}
                // Content-sized rows (overrides DeckView default 300px gridAutoRows).
                deckCardMinHeight={0}
                disableLocalPersistence
                defaultPageSize={10}
                searchPlaceholder={t("pro.system.certification.technicians.search")}
                emptyTitle={t("pro.system.certification.technicians.emptyTitle")}
                emptySubtitle={t("pro.system.certification.technicians.emptySubtitle")}
                headerActions={[
                  {
                    id: "legacy-claims",
                    label: t("pro.system.certification.claims.action"),
                    icon: <ArchiveRestore size={14} />,
                    variant: "outline",
                    onClick: () => navigate({ to: "/system/certifications/claims" }),
                  },
                ]}
              />
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          id={SECTION_IDS.rewardRules}
          title={t("pro.system.rewardRules.title")}
          subtitle={
            !rewardRulesLoading && rewardRules.length > 0
              ? `${rewardRules.length} rule${rewardRules.length !== 1 ? "s" : ""}`
              : undefined
          }
          icon={<Award size={20} />}
          expanded={isExpanded(SECTION_IDS.rewardRules)}
          onToggleExpanded={() => toggle(SECTION_IDS.rewardRules)}
        >
          <div className={layout.cardInner}>
            {rewardRules.length > 0 && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                <Button
                  color="primary"
                  variant="outline"
                  iconLeft={<Plus size={14} />}
                  onClick={() => {
                    setEditingRewardRule(undefined);
                    setRewardRuleModalOpen(true);
                  }}
                >
                  {t("pro.system.rewardRules.createButton")}
                </Button>
              </div>
            )}
            {rewardRulesError && (
              <div style={{ marginBottom: 16 }}>
                <Callout variant="error">{rewardRulesError}</Callout>
              </div>
            )}
            {rewardRulesLoading && rewardRules.length === 0 ? (
              <Text level="body-small" style={{ color: vars.color.fgMuted }}>
                {t("shared.common.loading")}
              </Text>
            ) : rewardRules.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 16,
                  padding: "32px 16px",
                  textAlign: "center",
                }}
              >
                <p style={{ fontSize: 14, color: vars.color.fgMuted, margin: 0, lineHeight: 1.45 }}>
                  {t("pro.system.rewardRules.empty")}
                </p>
                <Button
                  type="button"
                  color="primary"
                  variant="solid"
                  iconLeft={<Plus size={16} />}
                  onClick={() => {
                    setEditingRewardRule(undefined);
                    setRewardRuleModalOpen(true);
                  }}
                >
                  {t("pro.system.rewardRules.createButton")}
                </Button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {rewardRules.map((rule) => (
                  <RewardRuleCard
                    key={rule.id}
                    rule={rule}
                    onClick={() => {
                      setEditingRewardRule(rule);
                      setRewardRuleModalOpen(true);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </CollapsibleSection>
      </DetailPageLayout>

      <ModuleFormModal
        open={moduleModalOpen}
        onClose={() => setModuleModalOpen(false)}
        accessToken={token ?? ""}
        existing={editingModule}
        onSaved={(saved) => {
          setModules((prev) => {
            const idx = prev.findIndex((m) => m.id === saved.id);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = saved;
              return next;
            }
            return [...prev, saved];
          });
        }}
      />
      <ModuleConfirmModal />
      {importExportModals}

      <RewardRuleFormModal
        open={rewardRuleModalOpen}
        onClose={() => setRewardRuleModalOpen(false)}
        accessToken={token ?? ""}
        existing={editingRewardRule}
        onSaved={(saved) => {
          setRewardRules((prev) => {
            const idx = prev.findIndex((r) => r.id === saved.id);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = saved;
              return next;
            }
            return [...prev, saved];
          });
        }}
        onDeleted={(ruleId) => {
          setRewardRules((prev) => prev.filter((r) => r.id !== ruleId));
        }}
      />
    </>
  );
}
